// Installation settings registry (owner instruction PD-710; settings stream PD-665 – PD-689).
// Runtime values that used to be environment variables are edited by system administrators in the
// platform console, stored in passage.platform_setting and applied without a restart. Only the
// database connections, key rings and secrets that protect stored data, process/network facts,
// container paths, service wiring and the console hostname stay in the environment (ADR-031).
//
// Every entry has: a zod schema with bounds, the built-in default (the old environment default),
// its scope (installation now; `overridable` reserves organisation overrides for later), the
// legacy environment variable imported once (PD-668), whether it is sensitive (reason + confirmation)
// or a sealed write-only secret, a production guard where one existed before, and its group.
// Labels and help texts are i18n keys `set.k.<key>.label` / `set.k.<key>.help` (en + ar catalogues;
// a unit test checks both exist for every key). Validation messages are i18n keys.
import { z } from 'zod';

export const SETTING_GROUPS = ['security', 'email', 'integrations', 'malware', 'ai', 'worker', 'rateLimits', 'exports', 'devices'] as const;
export type SettingGroup = (typeof SETTING_GROUPS)[number];

/** Facts a default or guard may depend on (from the environment; never from the database). */
export interface SettingContext {
  readonly production: boolean;
  /** CLAMAV_HOST is set: the ClamAV service wiring exists (PD-674). */
  readonly clamavWired: boolean;
}

export type SettingUnit = 'ms' | 'seconds' | 'hours' | 'days' | 'bytes' | 'kib' | 'perMinute' | 'count';

export type SettingInput =
  | { readonly kind: 'integer'; readonly min: number; readonly max: number; readonly unit: SettingUnit; readonly productionMin?: number }
  | { readonly kind: 'boolean' }
  | { readonly kind: 'enum'; readonly options: readonly string[] }
  | { readonly kind: 'list'; readonly options: readonly string[] }
  | { readonly kind: 'text'; readonly maxLength: number; readonly nullable: boolean }
  | { readonly kind: 'secret' };

/** Where a value takes effect (shown in the console; documentation of the read path). */
export type SettingConsumer = 'api' | 'worker' | 'both';

export interface SettingDefinition<T> {
  readonly key: string;
  readonly group: SettingGroup;
  readonly input: SettingInput;
  readonly schema: z.ZodType<T>;
  readonly defaultValue: (ctx: SettingContext) => T;
  /** Legacy environment variable, imported once when the database has no row (PD-668). */
  readonly env: string | null;
  /** Changes need a reason and an explicit confirmation (security posture, outbound reach). */
  readonly sensitive: boolean;
  /** Sealed, write-only; never returned, logged or audited as a value (PD-669). */
  readonly secret: boolean;
  readonly scope: 'installation';
  /** Reserved: organisation overrides are not built yet (PD-666). */
  readonly overridable: false;
  readonly consumer: SettingConsumer;
  /** Problem code (i18n key) when the value is not acceptable in this context (production guards). */
  readonly guard?: (value: T, ctx: SettingContext) => SettingProblem | null;
}

export interface SettingProblem {
  readonly code: string;
  readonly params?: Readonly<Record<string, string | number>>;
}

type Opts = { sensitive?: boolean; consumer?: SettingConsumer; productionMin?: number };

function integer(key: string, group: SettingGroup, env: string | null, min: number, max: number, def: number, unit: SettingUnit, o: Opts = {}): SettingDefinition<number> {
  const productionMin = o.productionMin;
  return {
    key, group, env, scope: 'installation', overridable: false, secret: false, sensitive: o.sensitive ?? false, consumer: o.consumer ?? 'api',
    input: { kind: 'integer', min, max, unit, ...(productionMin !== undefined ? { productionMin } : {}) },
    schema: z.number({ error: 'v.invalid' }).int('v.invalid').min(min, 'v.range').max(max, 'v.range'),
    defaultValue: () => def,
    ...(productionMin !== undefined ? { guard: (v: number, ctx: SettingContext) => (ctx.production && v < productionMin ? { code: 'set.p.productionMinimum', params: { min: productionMin } } : null) } : {}),
  };
}

function boolean(key: string, group: SettingGroup, env: string | null, def: boolean | ((ctx: SettingContext) => boolean), o: Opts & { guard?: SettingDefinition<boolean>['guard'] } = {}): SettingDefinition<boolean> {
  return {
    key, group, env, scope: 'installation', overridable: false, secret: false, sensitive: o.sensitive ?? false, consumer: o.consumer ?? 'api',
    input: { kind: 'boolean' },
    schema: z.boolean({ error: 'v.invalid' }),
    defaultValue: typeof def === 'function' ? def : () => def,
    ...(o.guard ? { guard: o.guard } : {}),
  };
}

function choice<const O extends readonly [string, ...string[]]>(key: string, group: SettingGroup, env: string | null, options: O, def: O[number] | ((ctx: SettingContext) => O[number]), o: Opts & { guard?: SettingDefinition<O[number]>['guard'] } = {}): SettingDefinition<O[number]> {
  return {
    key, group, env, scope: 'installation', overridable: false, secret: false, sensitive: o.sensitive ?? false, consumer: o.consumer ?? 'api',
    input: { kind: 'enum', options },
    schema: z.enum(options, { error: 'v.invalid' }) as unknown as z.ZodType<O[number]>,
    defaultValue: typeof def === 'function' ? def : () => def,
    ...(o.guard ? { guard: o.guard } : {}),
  };
}

/** Hand-written entries keep their value type explicit. */
const entry = <T,>(d: SettingDefinition<T>): SettingDefinition<T> => d;

const notInProduction = <T,>(forbidden: T, code: string) => (v: T, ctx: SettingContext): SettingProblem | null => (ctx.production && v === forbidden ? { code } : null);

const AI_ADAPTERS = ['local-rules', 'anthropic'] as const;
const MODEL_ID = /^[a-z0-9][a-z0-9.@:_-]{1,119}$/;
const GEO = /^[a-z0-9][a-z0-9_-]{0,39}$/;

/**
 * The registry. Keys are stable identifiers (also the i18n label keys); `env` names the variable
 * that set the value before PD-710. Bounds and defaults are the previous environment ones.
 */
export const SETTINGS = {
  // ------------------------------------------------------------------ security & sign-in
  // PD-671: argon2id cost parameters. Stored hashes keep their own parameters; a change applies to
  // new hashes and to rehash-on-login (verifyPassword → needsRehash). Production keeps a safe floor
  // (OWASP minimum: 19 MiB, 2 passes); smaller values exist only for test databases.
  'password.hashMemoryKiB': integer('password.hashMemoryKiB', 'security', 'PASSWORD_HASH_MEMORY_KIB', 8 * 1024, 4 * 1024 * 1024, 65536, 'kib', { sensitive: true, productionMin: 19 * 1024 }),
  'password.hashPasses': integer('password.hashPasses', 'security', 'PASSWORD_HASH_PASSES', 1, 10, 3, 'count', { sensitive: true, productionMin: 2 }),
  'password.hashParallelism': integer('password.hashParallelism', 'security', 'PASSWORD_HASH_PARALLELISM', 1, 16, 4, 'count', { sensitive: true }),
  // ADR-017: identity simulators (UAE PASS, Emirates ID reader, MRZ/OCR) never in production.
  'verification.simulators': boolean('verification.simulators', 'security', 'VERIFICATION_SIMULATORS', (ctx) => !ctx.production, { sensitive: true, guard: notInProduction(true, 'set.p.simulatorProduction') }),

  // ------------------------------------------------------------------ email and messages
  'mail.smtpTimeoutMs': integer('mail.smtpTimeoutMs', 'email', 'MAIL_SMTP_TIMEOUT_MS', 1000, 120_000, 20_000, 'ms', { consumer: 'both' }),
  'mail.organisationPrivateNetworks': boolean('mail.organisationPrivateNetworks', 'email', 'MAIL_ORG_ALLOW_PRIVATE_NETWORKS', false, { sensitive: true, consumer: 'both' }),
  // PD-046: no SMS/WhatsApp provider is chosen (H-05); "local" = development outbox folders.
  'sms.provider': choice('sms.provider', 'email', 'SMS_PROVIDER', ['none', 'local'], 'none', { consumer: 'worker', guard: notInProduction<'none' | 'local'>('local', 'set.p.localOutboxProduction') }),
  'whatsapp.provider': choice('whatsapp.provider', 'email', 'WHATSAPP_PROVIDER', ['none', 'local'], 'none', { consumer: 'worker', guard: notInProduction<'none' | 'local'>('local', 'set.p.localOutboxProduction') }),

  // ------------------------------------------------------------------ integrations
  'api.tokenDays': integer('api.tokenDays', 'integrations', 'API_TOKEN_DAYS', 1, 365, 90, 'days'),
  'webhook.privateNetworks': boolean('webhook.privateNetworks', 'integrations', 'WEBHOOK_ALLOW_PRIVATE_NETWORKS', false, { sensitive: true, consumer: 'both' }),
  'webhook.timeoutMs': integer('webhook.timeoutMs', 'integrations', 'WEBHOOK_TIMEOUT_MS', 1000, 30_000, 10_000, 'ms', { consumer: 'both' }),
  'accessAdapter.privateNetworks': boolean('accessAdapter.privateNetworks', 'integrations', 'ACCESS_ADAPTER_ALLOW_PRIVATE_NETWORKS', false, { sensitive: true, consumer: 'worker' }),
  // PD-795 (hardening stream): organisation-configured OIDC/SAML endpoints and LDAP hosts on private
  // (RFC 1918 / ULA) or loopback addresses, e.g. an on-premises ADFS. Link-local and metadata never.
  'identity.allowPrivateNetworks': boolean('identity.allowPrivateNetworks', 'integrations', null, false, { sensitive: true, consumer: 'api' }),
  // ADR-022: only simulators exist (H-05); never in production.
  'calendar.providerMode': choice('calendar.providerMode', 'integrations', 'CALENDAR_PROVIDER_MODE', ['simulator', 'none'], (ctx) => (ctx.production ? 'none' : 'simulator'), { sensitive: true, guard: notInProduction<'simulator' | 'none'>('simulator', 'set.p.simulatorProduction') }),
  'calendar.providerTimeoutMs': integer('calendar.providerTimeoutMs', 'integrations', 'CALENDAR_PROVIDER_TIMEOUT_MS', 200, 60_000, 8000, 'ms'),
  'calendar.syncIntervalSeconds': integer('calendar.syncIntervalSeconds', 'integrations', 'CALENDAR_SYNC_INTERVAL_SECONDS', 0, 86_400, 0, 'seconds'),
  'directory.syncIntervalSeconds': integer('directory.syncIntervalSeconds', 'integrations', 'DIRECTORY_SYNC_INTERVAL_SECONDS', 0, 86_400, 0, 'seconds'),
  'directory.testSampleSize': integer('directory.testSampleSize', 'integrations', 'DIRECTORY_TEST_SAMPLE_SIZE', 1, 200, 25, 'count'),

  // ------------------------------------------------------------------ malware scanning (PD-707)
  // PD-674: "clamav" is selectable only when the ClamAV wiring (CLAMAV_HOST) exists; it is the
  // default where it does, so a deployment that wires clamd scans without a console step.
  'malware.scanner': choice('malware.scanner', 'malware', 'MALWARE_SCANNER', ['none', 'clamav'], (ctx) => (ctx.clamavWired ? 'clamav' : 'none'), {
    sensitive: true,
    guard: (v, ctx) => (v === 'clamav' && !ctx.clamavWired ? { code: 'set.p.clamavNotWired' } : null),
  }),
  'clamav.connectTimeoutMs': integer('clamav.connectTimeoutMs', 'malware', 'CLAMAV_CONNECT_TIMEOUT_MS', 100, 30_000, 2000, 'ms'),
  'clamav.scanTimeoutMs': integer('clamav.scanTimeoutMs', 'malware', 'CLAMAV_SCAN_TIMEOUT_MS', 1000, 300_000, 30_000, 'ms'),
  // Keep at or below clamd's StreamMaxLength; never below the largest upload (10 MiB, PD-038).
  'clamav.maxStreamBytes': integer('clamav.maxStreamBytes', 'malware', 'CLAMAV_MAX_STREAM_BYTES', 10 * 1024 * 1024, 2_147_483_647, 25 * 1024 * 1024, 'bytes'),
  'clamav.chunkBytes': integer('clamav.chunkBytes', 'malware', 'CLAMAV_CHUNK_BYTES', 1024, 1024 * 1024, 64 * 1024, 'bytes'),
  'clamav.signatureMaxAgeDays': integer('clamav.signatureMaxAgeDays', 'malware', 'CLAMAV_SIGNATURE_MAX_AGE_DAYS', 1, 365, 3, 'days'),

  // ------------------------------------------------------------------ AI add-ons (ADR-024)
  'ai.providers': entry<Array<(typeof AI_ADAPTERS)[number]>>({
    key: 'ai.providers', group: 'ai', env: 'AI_PROVIDERS', scope: 'installation', overridable: false, secret: false, sensitive: true, consumer: 'both',
    input: { kind: 'list', options: AI_ADAPTERS },
    schema: z.array(z.enum(AI_ADAPTERS, { error: 'v.invalid' })).max(AI_ADAPTERS.length).refine((a) => new Set(a).size === a.length, 'v.invalid'),
    defaultValue: () => ['local-rules'],
  }),
  'ai.providerTimeoutMs': integer('ai.providerTimeoutMs', 'ai', 'AI_PROVIDER_TIMEOUT_MS', 100, 120_000, 15_000, 'ms', { consumer: 'both' }),
  // PD-669: the installation Anthropic key, sealed with the identity key ring like organisation AI keys.
  'ai.anthropicApiKey': entry<string>({
    key: 'ai.anthropicApiKey', group: 'ai', env: 'AI_ANTHROPIC_API_KEY', scope: 'installation', overridable: false, secret: true, sensitive: true, consumer: 'both',
    input: { kind: 'secret' },
    schema: z.string().trim().min(10, 'set.v.secretLength').max(500, 'set.v.secretLength').regex(/^[\x21-\x7e]+$/, 'v.invalid'),
    defaultValue: () => '',
  }),
  // PD-604: model id is configuration; default per the current Claude API recommendation.
  'ai.anthropicModel': entry<string>({
    key: 'ai.anthropicModel', group: 'ai', env: 'AI_ANTHROPIC_MODEL', scope: 'installation', overridable: false, secret: false, sensitive: false, consumer: 'both',
    input: { kind: 'text', maxLength: 120, nullable: false },
    schema: z.string().trim().regex(MODEL_ID, 'set.v.modelId'),
    defaultValue: () => 'claude-opus-5-5',
  }),
  'ai.anthropicEffort': choice('ai.anthropicEffort', 'ai', 'AI_ANTHROPIC_EFFORT', ['low', 'medium', 'high'], 'medium', { consumer: 'both' }),
  'ai.anthropicMaxTokens': integer('ai.anthropicMaxTokens', 'ai', 'AI_ANTHROPIC_MAX_TOKENS', 1024, 64_000, 16_000, 'count', { consumer: 'both' }),
  'ai.anthropicMaxRetries': integer('ai.anthropicMaxRetries', 'ai', 'AI_ANTHROPIC_MAX_RETRIES', 0, 5, 2, 'count', { consumer: 'both' }),
  'ai.anthropicFallbacks': boolean('ai.anthropicFallbacks', 'ai', 'AI_ANTHROPIC_FALLBACKS', true, { consumer: 'both' }),
  'ai.anthropicInferenceGeo': entry<string | null>({
    key: 'ai.anthropicInferenceGeo', group: 'ai', env: 'AI_ANTHROPIC_INFERENCE_GEO', scope: 'installation', overridable: false, secret: false, sensitive: false, consumer: 'both',
    input: { kind: 'text', maxLength: 40, nullable: true },
    schema: z.string().trim().regex(GEO, 'v.invalid').nullable(),
    defaultValue: () => null,
  }),

  // ------------------------------------------------------------------ worker & jobs (ADR-007)
  // PD-675: the worker re-reads these at every loop, so a change applies on its next cycle.
  'worker.pollMs': integer('worker.pollMs', 'worker', 'WORKER_POLL_MS', 100, 60_000, 1000, 'ms', { consumer: 'worker' }),
  'worker.batchSize': integer('worker.batchSize', 'worker', 'WORKER_BATCH_SIZE', 1, 500, 20, 'count', { consumer: 'worker' }),
  'worker.leaseMs': integer('worker.leaseMs', 'worker', 'WORKER_LEASE_MS', 5000, 3_600_000, 60_000, 'ms', { consumer: 'worker' }),
  'worker.maxAttempts': integer('worker.maxAttempts', 'worker', 'WORKER_MAX_ATTEMPTS', 1, 100, 8, 'count', { consumer: 'worker' }),
  'worker.sweepMs': integer('worker.sweepMs', 'worker', 'WORKER_SWEEP_MS', 5000, 3_600_000, 60_000, 'ms', { consumer: 'worker' }),
  // PB-11-08 (PD-494): minimum interval between access-event polls of one connector (0 = off).
  'worker.accessEventPollMs': integer('worker.accessEventPollMs', 'worker', 'WORKER_ACCESS_EVENT_POLL_MS', 0, 86_400_000, 60_000, 'ms', { consumer: 'worker' }),
  'idempotency.retentionHours': integer('idempotency.retentionHours', 'worker', 'IDEMPOTENCY_RETENTION_HOURS', 1, 24 * 90, 72, 'hours'),
  // PD-257: scheduled retention planning in the API process (0 = off; planning never purges).
  'privacy.plannerIntervalSeconds': integer('privacy.plannerIntervalSeconds', 'worker', 'PRIVACY_PLANNER_INTERVAL_SECONDS', 0, 7 * 86_400, 0, 'seconds'),

  // ------------------------------------------------------------------ rate limits
  // ADR-020: the request limit a NEW API client gets (each client keeps its own stored limit).
  'api.rateLimitPerMinute': integer('api.rateLimitPerMinute', 'rateLimits', 'API_RATE_LIMIT_PER_MINUTE', 1, 100_000, 120, 'perMinute'),
  // PB-06-02 (PD-495): reception lookups per user and minute; plate / ID-number lookups lower.
  'reception.lookupPerMinute': integer('reception.lookupPerMinute', 'rateLimits', 'RECEPTION_LOOKUP_PER_MINUTE', 1, 10_000, 60, 'perMinute'),
  'reception.sensitiveLookupPerMinute': integer('reception.sensitiveLookupPerMinute', 'rateLimits', 'RECEPTION_SENSITIVE_LOOKUP_PER_MINUTE', 1, 1000, 10, 'perMinute'),

  // ------------------------------------------------------------------ exports, imports & reports
  'exports.ttlHours': integer('exports.ttlHours', 'exports', 'PASSAGE_EXPORT_TTL_HOURS', 1, 24 * 30, 24, 'hours', { consumer: 'both' }),
  'pdf.threads': integer('pdf.threads', 'exports', 'PASSAGE_PDF_THREADS', 1, 8, 2, 'count', { consumer: 'both' }),
  'pdf.timeoutMs': integer('pdf.timeoutMs', 'exports', 'PASSAGE_PDF_TIMEOUT_MS', 5000, 900_000, 120_000, 'ms', { consumer: 'both' }),
  // PD-676: the record-import and roster limits were undocumented environment reads; same rules.
  'imports.maxBytes': integer('imports.maxBytes', 'exports', 'RECORD_IMPORT_MAX_BYTES', 1024, 50 * 1024 * 1024, 5 * 1024 * 1024, 'bytes'),
  'imports.maxRows': integer('imports.maxRows', 'exports', 'RECORD_IMPORT_MAX_ROWS', 1, 100_000, 5000, 'count'),
  'imports.maxColumns': integer('imports.maxColumns', 'exports', 'RECORD_IMPORT_MAX_COLUMNS', 1, 200, 60, 'count'),
  'imports.maxUncompressedBytes': integer('imports.maxUncompressedBytes', 'exports', 'RECORD_IMPORT_MAX_UNCOMPRESSED_BYTES', 1024 * 1024, 500 * 1024 * 1024, 50 * 1024 * 1024, 'bytes'),
  'imports.chunkRows': integer('imports.chunkRows', 'exports', 'RECORD_IMPORT_CHUNK_ROWS', 1, 5000, 250, 'count'),
  'imports.rosterMaxRows': integer('imports.rosterMaxRows', 'exports', 'ROSTER_IMPORT_MAX_ROWS', 1, 1000, 200, 'count'),

  // ------------------------------------------------------------------ devices (ADR-020, ADR-025)
  'devices.staleSeconds': integer('devices.staleSeconds', 'devices', 'DEVICE_STALE_SECONDS', 30, 86_400, 300, 'seconds'),
  'edge.commandTtlSeconds': integer('edge.commandTtlSeconds', 'devices', 'EDGE_COMMAND_TTL_SECONDS', 15, 600, 120, 'seconds'),
} as const;

export type SettingKey = keyof typeof SETTINGS;
export type SettingValue<K extends SettingKey> = (typeof SETTINGS)[K] extends SettingDefinition<infer T> ? T : never;
export type SettingValues = { readonly [K in SettingKey]: SettingValue<K> };
/** Sealed, write-only settings (PD-669). A unit test keeps this list equal to the `secret` flags. */
export const SECRET_SETTING_KEYS = ['ai.anthropicApiKey'] as const;
export type SecretSettingKey = (typeof SECRET_SETTING_KEYS)[number];

export const SETTING_KEYS = Object.keys(SETTINGS) as SettingKey[];

export function isSettingKey(v: unknown): v is SettingKey {
  return typeof v === 'string' && Object.prototype.hasOwnProperty.call(SETTINGS, v);
}

export function settingDefinition<K extends SettingKey>(key: K): SettingDefinition<SettingValue<K>> {
  return SETTINGS[key] as unknown as SettingDefinition<SettingValue<K>>;
}

/** Built-in default in this context (the value in effect when nothing is stored). */
export function defaultSettingValue<K extends SettingKey>(key: K, ctx: SettingContext): SettingValue<K> {
  return settingDefinition(key).defaultValue(ctx);
}

export type SettingCheck<T> = { readonly ok: true; readonly value: T } | { readonly ok: false; readonly problem: SettingProblem };

/** Schema and context guard: the one validation used by the console, the env import and the reader. */
export function checkSettingValue<K extends SettingKey>(key: K, raw: unknown, ctx: SettingContext): SettingCheck<SettingValue<K>> {
  const def = settingDefinition(key);
  const parsed = def.schema.safeParse(raw);
  if (!parsed.success) {
    const issue = parsed.error.issues[0];
    const input = def.input;
    const params: Readonly<Record<string, number>> | null = input.kind === 'integer' ? { min: input.min, max: input.max } : input.kind === 'text' ? { max: input.maxLength } : null;
    return { ok: false, problem: { code: issue?.message && /^[a-z]+(\.[A-Za-z0-9]+)+$/.test(issue.message) ? issue.message : 'v.invalid', ...(params ? { params } : {}) } };
  }
  const problem = def.guard?.(parsed.data, ctx) ?? null;
  return problem ? { ok: false, problem } : { ok: true, value: parsed.data };
}

/**
 * The value a legacy environment string meant (PD-668). Returns undefined when the text cannot be
 * read as this setting's type; the caller then validates it like any console value.
 */
export function settingFromEnvironment(key: SettingKey, raw: string): unknown {
  const input = SETTINGS[key].input;
  const v = raw.trim();
  switch (input.kind) {
    case 'integer':
      return /^-?\d+$/.test(v) ? Number(v) : undefined;
    case 'boolean':
      return v === 'true' ? true : v === 'false' ? false : undefined;
    case 'enum':
    case 'secret':
      return v;
    case 'text':
      return v === '' && input.nullable ? null : v;
    case 'list':
      // AI_PROVIDERS: "none" was always implied; duplicates collapse.
      return [...new Set(v.split(',').map((s) => s.trim()).filter((s) => s && s !== 'none'))];
  }
}

/** Legacy variable → setting key (for the one-time import and the "ignored" warning). */
export const SETTING_BY_ENV: Readonly<Record<string, SettingKey>> = Object.fromEntries(
  SETTING_KEYS.filter((k) => SETTINGS[k].env).map((k) => [SETTINGS[k].env!, k]),
);

/**
 * Variables that are no longer read at all (PD-670, PD-672): public origins and link templates are
 * derived from organisation domains and PLATFORM_HOSTNAMES; the fallback sender is superseded by
 * the email delivery settings (PD-580).
 */
export const RETIRED_ENVIRONMENT_VARIABLES = ['ALLOWED_ORIGINS', 'INVITE_URL_TEMPLATE', 'SSO_BASE_URL_TEMPLATE', 'CALENDAR_NOTIFICATION_URL_TEMPLATE', 'MAIL_FROM'] as const;

// ------------------------------------------------------------------ console API

const Reason = z.string().trim().min(3, 'v.reasonShort').max(500, 'v.tooLong');

/** PUT /settings/:key — a value (non-secret) or a secret change; `expectedVersion` 0 = never stored. */
export const UpdateSettingRequest = z.object({
  expectedVersion: z.number().int().min(0),
  value: z.unknown().optional(),
  secret: z.discriminatedUnion('kind', [z.object({ kind: z.literal('set'), value: z.string().max(500, 'v.tooLong') }), z.object({ kind: z.literal('clear') })]).optional(),
  reason: Reason.optional(),
  /** Sensitive settings: the operator confirmed the consequence in the console. */
  confirmed: z.boolean().optional(),
});
export type UpdateSettingRequest = z.infer<typeof UpdateSettingRequest>;

export const ResetSettingRequest = z.object({ expectedVersion: z.number().int().min(0), reason: Reason.optional(), confirmed: z.boolean().optional() });
export type ResetSettingRequest = z.infer<typeof ResetSettingRequest>;

export type SettingSourceDto = 'default' | 'console' | 'environment';

export interface InstallationSettingDto {
  readonly key: SettingKey;
  readonly group: SettingGroup;
  readonly input: SettingInput;
  readonly sensitive: boolean;
  readonly secret: boolean;
  readonly consumer: SettingConsumer;
  /** Value in effect (null for a secret). */
  readonly value: unknown;
  /** Built-in default in this deployment (null for a secret). */
  readonly defaultValue: unknown;
  /** The value in effect equals the built-in default (whether or not a row is stored). */
  readonly isDefault: boolean;
  /** Secrets only: whether a value is stored. */
  readonly secretSet: boolean | null;
  readonly source: SettingSourceDto;
  /** Row version for optimistic concurrency (0 = never stored). */
  readonly version: number;
  readonly updatedAt: string | null;
  readonly updatedBy: string | null;
  /** A stored value that is NOT in effect (fails today's bounds or a production guard). */
  readonly storedProblem: SettingProblem | null;
  /** Legacy variable this setting replaced (documentation; it is no longer read after the import). */
  readonly environmentVariable: string | null;
}

/** Bootstrap facts that still come from the environment and are safe to show (PD-678). */
export interface EnvironmentFactsDto {
  readonly nodeEnv: string;
  readonly deploymentProfile: string;
  readonly appVersion: string | null;
  readonly consoleHostnames: readonly string[];
  readonly publicScheme: 'https' | 'http';
  readonly publicPort: number | null;
  /** Origins accepted for state-changing requests, derived from organisation domains (PD-670). */
  readonly allowedOrigins: readonly string[];
  /** Organisation hostnames left out by the production hostname guard. */
  readonly excludedHostnames: ReadonlyArray<{ readonly hostname: string; readonly reason: string }>;
  readonly clamavWired: boolean;
  readonly sealingKeyRings: { readonly identity: boolean };
  /** Moved variables still present in the environment and ignored (imported once or overruled). */
  readonly ignoredVariables: readonly string[];
  /** Retired variables still present in the environment (never read). */
  readonly retiredVariables: readonly string[];
  /** PD-720: IS_DEMO — demo mode (read-only, nothing sent). */
  readonly demo: boolean;
}

export interface InstallationSettingsResponse {
  readonly version: number;
  readonly groups: readonly SettingGroup[];
  readonly items: readonly InstallationSettingDto[];
  readonly environment: EnvironmentFactsDto;
}
